import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Section components
import SectionArticle from './SectionArticle';
import SectionCarousel from './SectionCarousel';
import SectionColumns from './SectionColumns';
import SectionFeatures from './SectionFeatures';
import SectionHero from './SectionHero';
import SectionNewListings from '../../LandingPage/LiveListingSection/SectionNewListings';
import SellerBuyerInfoSection from '../../LandingPage/SellerBuyerInfoSection/SellerBuyerInfoSection';

// Styles
// Note: these contain
// - shared classes that are passed as defaultClasses
// - dark theme overrides
// TODO: alternatively, we could consider more in-place way of theming components
import css from './SectionBuilder.module.css';
import SectionFooter from './SectionFooter';
import SectionDynamicFeaturedListing from '../../LandingPage/LiveListingSection/SectionDynamicFeaturedListing';
import { NamedLink, PrimaryButton } from '../../../components';

// These are shared classes.
// Use these to have consistent styles between different section components
// E.g. share the same title styles
const DEFAULT_CLASSES = {
  sectionDetails: css.sectionDetails,
  title: css.title,
  description: css.description,
  ctaButton: css.ctaButton,
  blockContainer: css.blockContainer,
};

/////////////////////////////////////////////
// Mapping of section types and components //
/////////////////////////////////////////////

const defaultSectionComponents = {
  article: { component: SectionArticle },
  carousel: { component: SectionCarousel },
  columns: { component: SectionColumns },
  features: { component: SectionFeatures },
  footer: { component: SectionFooter },
  hero: { component: SectionHero },
};

//////////////////////
// Section builder //
//////////////////////

/**
 * @typedef {Object} FieldOption
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * @typedef {Object} BlockOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionConfig
 * @property {string} sectionId
 * @property {string} sectionName
 * @property {('article' | 'carousel' | 'columns' | 'features' | 'hero')} sectionType
 */

/**
 * Build section elements from given section config array.
 *
 * @component
 * @param {Object} props
 * @param {Array<SectionConfig>} props.sections
 * @param {Object} props.options
 * @param {Object<string,FieldOption>} props.options.fieldComponents
 * @param {Object<string,BlockOption>} props.options.blockComponents
 * @param {Object<string,SectionOption>} props.options.sectionComponents
 * @param {boolean} props.options.isInsideContainer
 * @returns {JSX.Element} element containing array of sections according from given config array.
 */
const SectionBuilder = props => {
  const { sections = [], options } = props;
  const { sectionComponents = {}, isInsideContainer, ...otherOption } = options || {};

  // If there's no sections, we can't render the correct section component
  if (!sections || sections.length === 0) {
    return null;
  }

  // Selection of Section components
  const components = { ...defaultSectionComponents, ...sectionComponents };
  const getComponent = sectionType => {
    const config = components[sectionType];
    return config?.component;
  };

  // Generate unique ids for sections if operator has managed to create duplicates
  // E.g. "foobar", "foobar1", and "foobar2"
  const sectionIds = [];
  const getUniqueSectionId = (sectionId, index) => {
    const candidate = sectionId || `section-${index + 1}`;
    if (sectionIds.includes(candidate)) {
      let sequentialCandidate = `${candidate}1`;
      for (let i = 2; sectionIds.includes(sequentialCandidate); i++) {
        sequentialCandidate = `${candidate}${i}`;
      }
      return getUniqueSectionId(sequentialCandidate, index);
    } else {
      sectionIds.push(candidate);
      return candidate;
    }
  };
  const mapStateToPropsFactory = key => state => {
    const listings = state?.LandingPage?.[key] || [];
    const isLoading = state?.LandingPage?.[`${key}InProgress`] || false;
    const error = state?.LandingPage?.[`${key}Error`] || null;
    return { listings, isLoading, error };
  };

  const ConnectedSectionNewListings = ({ selectorKey, title }) => {
    const mapStateToProps = mapStateToPropsFactory(selectorKey);

    const Component = connect(mapStateToProps)(props => {
      if (selectorKey === 'newlyAddedListings') {
        return <SectionNewListings {...props} title={title} />;
      } else if (selectorKey === 'featuredListings') {
        return <SectionDynamicFeaturedListing {...props} title={title} />;
      } else {
        console.warn(`Unknown selectorKey: ${selectorKey}`);
        return null;
      }
    });

    return <Component />;
  };

  return (
    <>
      {sections.map((section, index) => {
        const Section = getComponent(section.sectionType);

        const isDarkTheme =
          section?.appearance?.fieldType === 'customAppearance' &&
          section?.appearance?.textColor === 'white';
        const classes = classNames({ [css.darkTheme]: isDarkTheme });
        const sectionId = getUniqueSectionId(section.sectionId, index);

        // Check if it's a live or featured listing section
        const isLiveListingSection = section.sectionName === 'Live listings';
        const isFeaturedListingSection = section.sectionName === 'Dynamic featured Listings';
        const isSellerBuyerInfoSection = section.sectionName === 'How it works';
        if (Section || isLiveListingSection || isFeaturedListingSection) {
          return (
            <React.Fragment key={`${sectionId}_wrap`}>
              {isLiveListingSection || isFeaturedListingSection ? (
                <ConnectedSectionNewListings
                  selectorKey={isLiveListingSection ? 'newlyAddedListings' : 'featuredListings'}
                  title={
                    section.sectionTitle ||
                    (isLiveListingSection ? 'New Listings' : 'Featured Listings')
                  }
                />
              ) : isSellerBuyerInfoSection ? (
                <SellerBuyerInfoSection></SellerBuyerInfoSection>
              ) : section.sectionName == 'Featured Lisitings' ? null : (
                <div className={css.ContainerHero}>
                  {section?.sectionName == 'Marketplace introduction' ? (
                    <>
                      <div className={css.LandingSearch}>
                        <h4>Buy and sell your outdoor equipment</h4>
                        <NamedLink name="NewListingPage">
                          <PrimaryButton>Sell Your Gear</PrimaryButton>
                        </NamedLink>
                        <p>
                          <NamedLink
                            name="CMSPage"
                            params={{ pageId: 'user-guide' }}
                          >
                            Learn how it works
                          </NamedLink>
                        </p>
                      </div>
                      <div>
                        <Section
                          key={`${sectionId}_i${index}`}
                          className={classes}
                          defaultClasses={DEFAULT_CLASSES}
                          isInsideContainer={isInsideContainer}
                          options={otherOption}
                          {...section}
                          sectionId={'sectionHeroCustom'}
                        />
                      </div>
                    </>
                  ) 
                  :(
                    <Section
                      key={`${sectionId}_i${index}`}
                      className={classes}
                      defaultClasses={DEFAULT_CLASSES}
                      isInsideContainer={isInsideContainer}
                      options={otherOption}
                      {...section}
                      sectionId={sectionId}
                    />
                  )}
                </div>
              )}
            </React.Fragment>
          );
        } else {
          console.warn(
            `Unknown section type (${section.sectionType}) detected using sectionName (${section.sectionName}).`
          );
          return null;
        }
      })}
    </>
  );
};

export default SectionBuilder;
